/**
 * Hash routing helpers: push/replace history entries and fire hashchange so App.tsx
 * and other listeners stay in sync with direct `location.hash` assignment.
 *
 * Use pushHashRoute when closing a detail view (or returning to a list) so the
 * browser Back button can restore the previous URL (e.g. .../view/{id}).
 */

function normalizeHashFragment(hash: string): string {
    const t = hash.trim();
    return t.startsWith('#') ? t : `#${t}`;
}

/** Hash path only (no leading #), lowercased — for comparing routes ignoring query string. */
function hashPathKey(hash: string): string {
    return normalizeHashFragment(hash).split('?')[0].toLowerCase();
}

/** True when the current location hash path already matches the given route (ignores ?search in hash). */
export function isCurrentHashPath(hash: string): boolean {
    if (typeof window === 'undefined') return false;
    return hashPathKey(window.location.hash) === hashPathKey(hash);
}

/** Full URL string for history APIs (preserves pathname + search). */
function urlWithHash(hashFragment: string): string {
    const u = new URL(window.location.href);
    u.hash = normalizeHashFragment(hashFragment);
    return u.toString();
}

const NAV_STACK_KEY = 'hades.hashNav.stack.v1';

function getNavStack(): string[] {
    try {
        const raw = window.sessionStorage.getItem(NAV_STACK_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        if (Array.isArray(parsed)) return parsed.filter((s) => typeof s === 'string');
        return [];
    } catch {
        return [];
    }
}

function setNavStack(stack: string[]): void {
    try {
        window.sessionStorage.setItem(NAV_STACK_KEY, JSON.stringify(stack.slice(-50)));
    } catch {
        // ignore
    }
}

function recordCurrentUrlAsPush(previousHref?: string): void {
    const href = window.location.href;
    const stack = getNavStack();
    const normalizedPreviousHref = previousHref && previousHref !== href ? previousHref : null;
    if (normalizedPreviousHref && (stack.length === 0 || stack[stack.length - 1] !== normalizedPreviousHref)) {
        stack.push(normalizedPreviousHref);
    }
    if (stack.length === 0 || stack[stack.length - 1] !== href) stack.push(href);
    setNavStack(stack);
}

function recordCurrentUrlAsReplace(): void {
    const href = window.location.href;
    const stack = getNavStack();
    if (stack.length === 0) {
        stack.push(href);
    } else {
        stack[stack.length - 1] = href;
    }
    setNavStack(stack);
}

function getPreviousRecordedUrl(): string | null {
    const stack = getNavStack();
    if (stack.length < 2) return null;
    return stack[stack.length - 2] ?? null;
}

function isSafeInAppUrl(url: string): boolean {
    try {
        const u = new URL(url);
        if (u.origin !== window.location.origin) return false;
        // Require a hash-route that looks like our app navigation.
        const h = (u.hash ?? '').trim();
        if (!h.startsWith('#')) return false;
        if (h === '#') return false;
        // Guard against accidental back into auth flows / MSAL redirect attempts.
        const lower = (h + ' ' + u.search + ' ' + u.pathname).toLowerCase();
        if (lower.includes('msal')) return false;
        if (lower.includes('signin-oidc')) return false;
        if (lower.includes('auth')) return false;
        if (lower.includes('code=')) return false;
        if (lower.includes('id_token=')) return false;
        if (lower.includes('access_token=')) return false;
        if (lower.includes('error=')) return false;
        return true;
    } catch {
        return false;
    }
}

export function pushHashRoute(hash: string): void {
    const previousHref = window.location.href;
    const nextUrl = urlWithHash(hash);
    if (previousHref === nextUrl) return;
    window.history.pushState(null, '', nextUrl);
    window.dispatchEvent(new Event('hashchange'));
    recordCurrentUrlAsPush(previousHref);
}

/**
 * Prefer going back (pop) to avoid creating a new history entry.
 * Only uses back when the previous entry is a safe in-app URL (to avoid MSAL redirect/callback entries).
 */
export function backOrReplaceHashRoute(fallbackHash: string): void {
    const prev = getPreviousRecordedUrl();
    if (prev && window.history.length > 1 && isSafeInAppUrl(prev)) {
        window.history.back();
        return;
    }

    replaceHashRoute(fallbackHash);
}

/**
 * replaceState + hashchange — e.g. normalizing an invalid URL or syncing search in the
 * address bar without growing the stack.
 */
export function replaceHashRoute(hash: string): void {
    window.history.replaceState(null, '', urlWithHash(hash));
    window.dispatchEvent(new Event('hashchange'));
    recordCurrentUrlAsReplace();
}

/**
 * pushHashRoute while temporarily forcing scroll-behavior to auto (reduces jump when
 * clearing hash-driven UI; see PersonManagement).
 */
export function pushHashRouteSuppressSmoothScroll(hash: string): void {
    const original = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';
    pushHashRoute(hash);
    setTimeout(() => {
        document.documentElement.style.scrollBehavior = original;
    }, 10);
}
